import { type Candle, InstrumentId } from "@trader/shared/MarketData"
import { Option, Schema } from "effect"

// The watchlist (docs/design/0014), as TradingView's: symbols in order, grouped by optional
// section headers that fold. Pure: the store saves it, the panel draws it.

export const WatchSymbol = Schema.TaggedStruct("Symbol", { instrumentId: InstrumentId })
export const WatchSection = Schema.TaggedStruct("Section", {
  id: Schema.String,
  title: Schema.String,
  collapsed: Schema.Boolean
})
export const WatchEntry = Schema.Union([WatchSymbol, WatchSection])
export type WatchEntry = typeof WatchEntry.Type
export type WatchSection = typeof WatchSection.Type

/** The saved document ("watchlist"); version 1. */
export const WatchlistDocument = Schema.Struct({ version: Schema.Literal(1), entries: Schema.Array(WatchEntry) })
export type Watchlist = typeof WatchlistDocument.Type

/** At most this many symbols: each one's quote is polled. */
export const MAX_SYMBOLS = 60

const symbol = (id: string): WatchEntry => ({ _tag: "Symbol", instrumentId: InstrumentId.make(id) })

/** What a new user starts with: the CFDs and indices, then a few NSE stocks. */
export const DEFAULT_WATCHLIST: Watchlist = {
  version: 1,
  entries: [
    { _tag: "Section", id: "indices", title: "Indices & CFDs", collapsed: false },
    ...["OANDA:NAS100USD", "OANDA:DE30EUR", "OANDA:JP225USD", "OANDA:XAUUSD", "NSE:NIFTY50", "NSE:BANKNIFTY"].map(symbol),
    { _tag: "Section", id: "stocks", title: "Stocks", collapsed: false },
    ...["NSE:RELIANCE", "NSE:HDFCBANK", "NSE:TCS", "NSE:INFY"].map(symbol)
  ]
}

const decode = Schema.decodeUnknownOption(Schema.fromJsonString(WatchlistDocument))

/** A saved watchlist, or null when it's missing or not one. */
export const decodeWatchlist = (json: string | null): Watchlist | null => (json === null ? null : Option.getOrNull(decode(json)))

export const symbolsOf = (list: Watchlist): ReadonlyArray<InstrumentId> =>
  list.entries.flatMap((entry) => (entry._tag === "Symbol" ? [entry.instrumentId] : []))

const has = (list: Watchlist, id: InstrumentId) => list.entries.some((entry) => entry._tag === "Symbol" && entry.instrumentId === id)

/**
 * Adds a symbol at the end of `sectionId`'s group (before the next section), or at the end of the
 * list; nothing if it's already there or the list is full.
 */
export const addSymbol = (list: Watchlist, id: InstrumentId, sectionId: string | null = null): Watchlist => {
  if (has(list, id) || symbolsOf(list).length >= MAX_SYMBOLS) return list
  const entries = [...list.entries]
  const start = sectionId === null ? -1 : entries.findIndex((entry) => entry._tag === "Section" && entry.id === sectionId)
  if (start < 0) return { ...list, entries: [...entries, symbol(id)] }
  const next = entries.findIndex((entry, i) => i > start && entry._tag === "Section")
  entries.splice(next < 0 ? entries.length : next, 0, symbol(id))
  return { ...list, entries }
}

export const removeSymbol = (list: Watchlist, id: InstrumentId): Watchlist => ({
  ...list,
  entries: list.entries.filter((entry) => !(entry._tag === "Symbol" && entry.instrumentId === id))
})

export const addSection = (list: Watchlist, id: string, title: string): Watchlist => ({
  ...list,
  entries: [...list.entries, { _tag: "Section", id, title: title.trim() || "Section", collapsed: false }]
})

const editSection = (list: Watchlist, id: string, edit: (section: WatchSection) => WatchSection): Watchlist => ({
  ...list,
  entries: list.entries.map((entry) => (entry._tag === "Section" && entry.id === id ? edit(entry) : entry))
})

export const toggleSection = (list: Watchlist, id: string): Watchlist =>
  editSection(list, id, (section) => ({ ...section, collapsed: !section.collapsed }))

export const renameSection = (list: Watchlist, id: string, title: string): Watchlist =>
  editSection(list, id, (section) => ({ ...section, title: title.trim() || section.title }))

/** Removes a section header; its symbols stay, joining the group above. */
export const removeSection = (list: Watchlist, id: string): Watchlist => ({
  ...list,
  entries: list.entries.filter((entry) => !(entry._tag === "Section" && entry.id === id))
})

/** Moves the entry at `from` to `to` (indices in `entries`), as a drag in the panel does. */
export const moveEntry = (list: Watchlist, from: number, to: number): Watchlist => {
  if (from === to || from < 0 || from >= list.entries.length) return list
  const entries = [...list.entries]
  const [moved] = entries.splice(from, 1)
  entries.splice(Math.max(0, Math.min(to, entries.length)), 0, moved!)
  return { ...list, entries }
}

/** The rows to draw: every section, and the symbols of the open ones (with their index in `entries`). */
export const visibleRows = (list: Watchlist): ReadonlyArray<{ readonly entry: WatchEntry; readonly index: number }> => {
  const rows: Array<{ readonly entry: WatchEntry; readonly index: number }> = []
  let folded = false
  list.entries.forEach((entry, index) => {
    if (entry._tag === "Section") folded = entry.collapsed
    if (entry._tag === "Section" || !folded) rows.push({ entry, index })
  })
  return rows
}

// --- quotes ---------------------------------------------------------------------------------------

export interface Quote {
  /** The last bar's close (the price now, or at the replay's time). */
  readonly last: number
  /** Against the previous day's close; null without one. */
  readonly change: number | null
  readonly changePercent: number | null
  /** The last bar's open time. */
  readonly time: number
}

/** A symbol's quote from its recent daily bars (oldest first): the last close against the one before. */
export const quoteOf = (bars: ReadonlyArray<Candle>): Quote | null => {
  const last = bars.at(-1)
  if (last === undefined) return null
  const previous = bars.at(-2)
  const change = previous === undefined ? null : last.close - previous.close
  return {
    last: last.close,
    change,
    changePercent: change === null || previous === undefined || previous.close === 0 ? null : (change / previous.close) * 100,
    time: last.time
  }
}
